import { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Modal } from '../../components/Modal';
import { api } from '../../lib/api';
import type { LinhaReposicao } from './Reposicoes';

// O painel que ABRE O PACOTE ([DA-364]). Irmao do `PainelTransferir` na forma, OPOSTO na regra —
// e a diferenca merece ser gritada, porque as duas acoes moram lado a lado na mesma linha da
// mesma tela:
//
//   Repor      → um produto so, troca de prateleira. O total NAO muda, e por isso nao cascateia.
//   Fracionar  → DOIS produtos. O pai perde N, o filho ganha N x fator. O total muda nos dois, e
//                por isso a cascata de prateleira e obrigatoria ([DA-356] decisao 4).
//
// Chamar as duas de "transferir" seria o convite pra alguem reusar a rota errada daqui a tres
// meses. O nome na tela e o nome no razao (`fracionamento_saida`/`fracionamento_entrada`).
//
// A TRAVA DE SALDO AQUI E LEGITIMA, igual a da transferencia e ao contrario da venda ([DA-329]):
// quem fraciona esta de pe na frente da prateleira com a caixa na mao.

const num = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const fmt = (v: unknown) => String(num(v)).replace('.', ',');

interface Props {
  linha: LinhaReposicao | null;
  onFechar: () => void;
  onPronto: () => void;
}

export function PainelFracionar({ linha, onFechar, onPronto }: Props) {
  const [qtdPai, setQtdPai] = useState<string>('1');
  const [origemId, setOrigemId] = useState<number>(0);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const frac = linha?.fracionavel ?? null;
  // De qual prateleira sai o pacote. O pai tem endereco como qualquer produto, e o servidor nao tem
  // como adivinhar de qual caixa a pessoa tirou: sem escolha ele drena a PRINCIPAL primeiro, que e
  // a presuncao da venda e nao a do deposito. Vazio = deixar a cascata decidir (o de sempre).
  const lugaresPai = frac?.pai_lugares ?? [];

  // Quantos pacotes sugerir: o suficiente pra o filho passar do minimo, nunca menos que 1 e nunca
  // mais do que o pai tem. Quem esta com a caixa na mao confirma, nao calcula — mesma regra do
  // `sugerido` da reposicao.
  useEffect(() => {
    if (!linha || !frac) return;
    const fator = num(frac.fator);
    const falta = num(linha.vitrine_minimo) - num(linha.estoque_atual);
    const precisa = fator > 0 && falta > 0 ? Math.ceil(falta / fator) : 1;
    const cabe = Math.max(1, Math.min(precisa, Math.floor(num(frac.pai_estoque)) || 1));
    setQtdPai(String(cabe));
    // Ja vem escolhida a prateleira mais CHEIA (a lista chega ordenada por quantidade) — e de la que
    // se tira uma caixa sem esvaziar nada. Quem esta na frente da prateleira confirma, nao procura.
    setOrigemId(frac.pai_lugares?.[0]?.localizacao_id ?? 0);
    setErro(null);
  }, [linha, frac]);

  if (!linha || !frac) return null;

  const fator = num(frac.fator);
  const pais = num(qtdPai.replace(',', '.'));
  const temPai = num(frac.pai_estoque);
  const geraFilho = Number((pais * fator).toFixed(3));
  const excedeu = pais > temPai;
  const lugarEscolhido = lugaresPai.find((l) => l.localizacao_id === origemId) ?? null;
  const temNoLugar = lugarEscolhido ? num(lugarEscolhido.quantidade) : 0;
  // Trava espelhando o backend — existe pra ele VER antes de clicar, nunca pra ser mais rigida que
  // o servidor, que e quem garante de verdade (com a linha travada).
  const excedeuLugar = !!lugarEscolhido && pais > temNoLugar;
  const invalido = pais <= 0 || excedeu || excedeuLugar || salvando;

  async function fracionar() {
    if (!linha || !frac) return;
    setSalvando(true);
    setErro(null);
    try {
      await api.post(`/api/produtos/fracionamentos/${frac.frac_id}/executar`, {
        quantidade_pai: pais,
        localizacao_pai_id: origemId || undefined,
      });
      onPronto();
    } catch (e: any) {
      setErro(e?.corpo?.erro ?? e?.message ?? 'Nao foi possivel fracionar.');
      setSalvando(false);
    }
  }

  return (
    <Modal
      aberto
      onFechar={onFechar}
      titulo={`Fracionar — ${linha.codigo_interno ?? ''} ${linha.descricao}`}
      largura={560}
      rodape={
        <>
          <button type="button" className="btn-secundario" onClick={onFechar} disabled={salvando}>Cancelar</button>
          <button type="button" className="btn-primario" onClick={fracionar} disabled={invalido}>
            {salvando ? 'Fracionando...' : 'Confirmar fracionamento'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* O de-para: qual embalagem se abre e qual produto recebe. Mesma seta do painel de repor,
            porque tambem aqui a tela desenha o movimento fisico que a pessoa vai fazer. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }} className="muted">Abrir de</label>
            <div style={{ padding: '8px 14px', border: '1px solid var(--input-border)', borderRadius: 4, background: 'var(--input-bg)', color: 'var(--input-fg)' }}>
              <div style={{ fontWeight: 600 }}>{frac.pai_codigo} — {frac.pai_descricao}</div>
              <div className="cel-num" style={{ fontSize: 12 }}>tem {fmt(temPai)}</div>
            </div>
            {/* A prateleira do pacote. So vira ESCOLHA quando ha mais de uma — com um lugar so nao
                ha o que decidir, e um select de uma opcao so pede um clique que nao muda nada. */}
            {lugaresPai.length > 1 ? (
              <div style={{ marginTop: 6 }}>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }} className="muted">Tirar da prateleira</label>
                <select value={origemId} onChange={(e) => setOrigemId(Number(e.target.value))} style={{ width: '100%' }}>
                  {lugaresPai.map((o) => (
                    <option key={o.localizacao_id} value={o.localizacao_id}>
                      {o.codigo} — tem {fmt(o.quantidade)}
                    </option>
                  ))}
                </select>
              </div>
            ) : lugaresPai.length === 1 ? (
              <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                na prateleira {lugaresPai[0]!.codigo} — tem {fmt(lugaresPai[0]!.quantidade)}
              </div>
            ) : null}
          </div>
          <ArrowRight size={18} style={{ marginTop: 18, flex: '0 0 auto' }} />
          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }} className="muted">Vira</label>
            <div style={{ padding: '8px 14px', border: '1px solid var(--input-border)', borderRadius: 4, background: 'var(--input-bg)', color: 'var(--input-fg)' }}>
              <div style={{ fontWeight: 600 }}>{linha.codigo_interno} — {linha.descricao}</div>
              <div className="cel-num" style={{ fontSize: 12 }}>tem {fmt(linha.estoque_atual)}</div>
            </div>
          </div>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }} className="muted">
            Quantos abrir (do {frac.pai_codigo})
          </label>
          <input
            value={qtdPai}
            onChange={(e) => setQtdPai(e.target.value)}
            inputMode="decimal"
            style={{ width: 160, fontSize: 18, fontWeight: 700 }}
            autoFocus
          />
          <span className="muted" style={{ marginLeft: 10, fontSize: 13 }}>
            1 gera {fmt(fator)} {linha.unidade_sigla ?? 'un'}
          </span>
          {excedeu && (
            <p style={{ background: '#f97316', color: '#000', padding: '6px 10px', borderRadius: 3, marginTop: 8, fontSize: 13 }}>
              {frac.pai_codigo} tem {fmt(temPai)} — nao da pra abrir {fmt(pais)}.
            </p>
          )}
          {!excedeu && excedeuLugar && lugarEscolhido && (
            <p style={{ background: '#f97316', color: '#000', padding: '6px 10px', borderRadius: 3, marginTop: 8, fontSize: 13 }}>
              a prateleira {lugarEscolhido.codigo} tem {fmt(temNoLugar)} — escolha outra ou conte de novo.
            </p>
          )}
        </div>

        {/* O previa dos DOIS totais. E aqui que esta tela se separa da de repor: la a frase diz "o
            total nao muda"; aqui os dois totais mudam, e mostra-los antes do clique e o que impede
            o "isso vai mexer no meu estoque?" virar surpresa depois. */}
        {!excedeu && !excedeuLugar && pais > 0 && (
          <div style={{ background: '#fde68a', color: '#000', padding: '10px 12px', borderRadius: 3, fontSize: 13 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Depois de confirmar</div>
            <div className="cel-num">
              {frac.pai_codigo}: {fmt(temPai)} → <strong>{fmt(Number((temPai - pais).toFixed(3)))}</strong> (−{fmt(pais)})
            </div>
            <div className="cel-num">
              {linha.codigo_interno}: {fmt(linha.estoque_atual)} → <strong>{fmt(Number((num(linha.estoque_atual) + geraFilho).toFixed(3)))}</strong> (+{fmt(geraFilho)})
            </div>
            {/* A prateleira entra na previa pelo mesmo motivo dos totais: ela muda, e o que muda sem
                aparecer antes do clique vira surpresa depois. */}
            {lugarEscolhido && (
              <div className="cel-num">
                {lugarEscolhido.codigo}: {fmt(temNoLugar)} → <strong>{fmt(Number((temNoLugar - pais).toFixed(3)))}</strong> (−{fmt(pais)})
              </div>
            )}
          </div>
        )}

        <p className="muted" style={{ fontSize: 12, margin: 0 }}>
          O estoque total dos <strong>dois produtos muda</strong> — a mercadoria e a mesma, mas deixa
          de ser um pacote fechado e passa a ser {linha.unidade_sigla ?? 'unidade'} avulsa. As
          prateleiras acompanham: sai da do pai, entra na do filho.
        </p>

        {erro && (
          <p style={{ background: '#f97316', color: '#000', padding: '8px 12px', borderRadius: 3, fontSize: 13 }}>{erro}</p>
        )}
      </div>
    </Modal>
  );
}
